/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaMapMarkedAlt, FaLock } from 'react-icons/fa';

import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';
import { LinkButton } from '../../../../styles/ui/Link';

// =============================================================================
// Painel 1 — Informações do cliente (somente leitura).
//
// Todos os campos são read-only nesta ficha: os dados do cliente são um
// snapshot da importação e não fazem sentido de serem alterados na etapa de
// fechamento/ajuste (o financeiro só consome). Locked com cadeado + tooltip:
//   - customer_name / customer_phone (antes editáveis — travados por regra)
//   - service_address / service_address_complement
//   - order_value, customer_refiltek_id
//   - botão Maps (lat/lng)
//
// Componente de exibição pura — não recebe mais estado de edição.
// =============================================================================

const ReadOnlyValue = styled.div`
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
   padding: 6px 0;
   word-break: break-word;
`;

const Highlight = styled.div`
   font-size: 1rem;
   font-weight: 600;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

const LockBadge = styled(FaLock)`
   margin-left: 4px;
   opacity: 0.5;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#888'};
`;

const MapsButton = styled(LinkButton)`
   height: 36px;
   font-size: 0.8rem;
`;

function formatCurrency(value) {
   if (value === null || value === undefined || value === '') return '—';
   const numeric = Number(value);
   if (Number.isNaN(numeric)) return '—';
   return numeric.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

function buildGoogleMapsUrl(item) {
   const lat = item.address_latitude;
   const lng = item.address_longitude;

   // Prefere lat/long se houver — abre direto no ponto. Fallback: endereço como query.
   if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
   }
   if (item.service_address) {
      const fullAddress = [
         item.service_address,
         item.service_address_complement,
      ]
         .filter(Boolean)
         .join(', ');
      return `https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}`;
   }
   return null;
}

const IMPORT_LOCK_TOOLTIP = 'Dado vem da importação, não editável aqui.';
const ADDRESS_LOCK_TOOLTIP =
   'Edição de endereço será adicionada em breve — depende de ajuste de coordenadas no mapa.';
const CUSTOMER_LOCK_TOOLTIP =
   'Dados do cliente não são editáveis nesta etapa (snapshot da importação).';

export default function ItemCustomerPanel({ item }) {
   if (!item) return null;

   const mapsUrl = buildGoogleMapsUrl(item);
   const fullAddress = [item.service_address, item.service_address_complement]
      .filter(Boolean)
      .join(' — ');

   return (
      <Section>
         <SectionHeader>
            <SectionTitle>Cliente</SectionTitle>
         </SectionHeader>

         <SectionBody>
            <FormGrid $cols={12} $colsMobile={12}>
               {/* ─── Nome (locked: snapshot da importação) ─── */}
               <Field $col={6} $colMobile={12}>
                  <label title={CUSTOMER_LOCK_TOOLTIP}>
                     Nome do cliente
                     <LockBadge size={9} />
                  </label>
                  <Highlight title={CUSTOMER_LOCK_TOOLTIP}>
                     {item.customer_name ?? '—'}
                  </Highlight>
               </Field>

               {/* ─── Telefone (locked: snapshot da importação) ─ */}
               <Field $col={3} $colMobile={6}>
                  <label title={CUSTOMER_LOCK_TOOLTIP}>
                     Telefone
                     <LockBadge size={9} />
                  </label>
                  <ReadOnlyValue title={CUSTOMER_LOCK_TOOLTIP}>
                     {item.customer_phone ?? '—'}
                  </ReadOnlyValue>
               </Field>

               {/* ─── Valor do pedido (locked: vem do CSV) ──── */}
               <Field $col={3} $colMobile={6}>
                  <label title={IMPORT_LOCK_TOOLTIP}>
                     Valor do pedido
                     <LockBadge size={9} />
                  </label>
                  <Highlight title={IMPORT_LOCK_TOOLTIP}>
                     {formatCurrency(item.order_value)}
                  </Highlight>
               </Field>

               {/* ─── Endereço (locked: próxima sprint) ──────── */}
               <Field $col={9} $colMobile={12}>
                  <label title={ADDRESS_LOCK_TOOLTIP}>
                     Endereço
                     <LockBadge size={9} />
                  </label>
                  <ReadOnlyValue title={ADDRESS_LOCK_TOOLTIP}>
                     {fullAddress || '—'}
                  </ReadOnlyValue>
               </Field>

               {/* ─── Maps button (locked: lat/lng vêm do import) */}
               <Field $col={3} $colMobile={12} $alignEnd>
                  <label>&nbsp;</label>
                  {mapsUrl ? (
                     <MapsButton
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Abrir localização no Google Maps (coordenadas vêm da importação)"
                     >
                        <FaMapMarkedAlt size={14} />
                        Abrir no Maps
                     </MapsButton>
                  ) : (
                     <ReadOnlyValue style={{ opacity: 0.6 }}>
                        Sem coordenadas
                     </ReadOnlyValue>
                  )}
               </Field>

               {/* ─── ID Refiltek (locked, conditional) ─────── */}
               {item.customer_refiltek_id ? (
                  <Field $col={4} $colMobile={6}>
                     <label title={IMPORT_LOCK_TOOLTIP}>
                        ID Refiltek
                        <LockBadge size={9} />
                     </label>
                     <ReadOnlyValue title={IMPORT_LOCK_TOOLTIP}>
                        {item.customer_refiltek_id}
                     </ReadOnlyValue>
                  </Field>
               ) : null}
            </FormGrid>
         </SectionBody>
      </Section>
   );
}

ItemCustomerPanel.propTypes = {
   item: PropTypes.shape({
      customer_name: PropTypes.string,
      customer_phone: PropTypes.string,
      customer_refiltek_id: PropTypes.oneOfType([
         PropTypes.string,
         PropTypes.number,
      ]),
      order_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      service_address: PropTypes.string,
      service_address_complement: PropTypes.string,
      address_latitude: PropTypes.oneOfType([
         PropTypes.string,
         PropTypes.number,
      ]),
      address_longitude: PropTypes.oneOfType([
         PropTypes.string,
         PropTypes.number,
      ]),
   }),
};
